import React, { FormEventHandler, useCallback, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'

import TextInput from 'components/Common/Form/Input/TextInput'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import { connect } from 'react-redux'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { formToObject } from 'lib/forms/formToObject'
import BodyText from 'LuxKit/Typography/BodyText'
import { resetUserPassword } from 'actions/AuthActions'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import { selectLoggedIn } from 'selectors/accountSelectors'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { AccountAccessModalResult } from './AccountAccessModal'

interface ResetPasswordForm {
  password: string
  confirmPassword: string
}

interface Props {
  error?: any
  token: string
  callbackPath?: string
  dismissable?: boolean
}

function AccountAccessResetPassword(props: Props) {
  const {
    error,
    token,
    callbackPath,
    dismissable,
  } = props

  const intl = useIntl()
  const firstInput = useRef<HTMLInputElement>(null)
  const dispatch = useAppDispatch()
  const modalContext = useModalElementContext<AccountAccessModalResult>()
  const loggedIn = useAppSelector(selectLoggedIn)
  const authProcessing = useAppSelector(state => state.auth.processing)
  const authError = useAppSelector(state => state.auth.error)
  const [resetting, setResetting] = useState<boolean>(false)
  const [passwordError, setPasswordError] = useState<string | null>(null)

  useEffect(() => {
    if (resetting && !authProcessing) {
      // must be done with the reset operation
      setResetting(false)
      if (loggedIn && !authError) {
        // done and we're now logged in - must've been successful, close the modal
        modalContext.resolve({ loggedIn: true })
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetting, authProcessing, loggedIn, authError])

  useEffect(() => {
    firstInput.current?.focus()
  }, [])

  const passwordsDoNotMatchError = intl.formatMessage({
    id: 'ORjwEpKfCM',
    defaultMessage: 'Passwords do not match',
    description: 'Validation error when password and confirmation do not match during reset',
  })

  const onSubmit = useCallback<FormEventHandler<HTMLFormElement>>((event) => {
    event.preventDefault()
    const { password, confirmPassword } = formToObject<ResetPasswordForm>(event.currentTarget)
    if (password !== confirmPassword) {
      setPasswordError(passwordsDoNotMatchError)
      return
    }
    setPasswordError(null)
    setResetting(true)
    dispatch(resetUserPassword(token, password, callbackPath))
  }, [dispatch, token, callbackPath, passwordsDoNotMatchError])

  const modalTitle = intl.formatMessage({
    id: '/ig3Tff4Wa',
    defaultMessage: 'Update your password',
    description: 'Modal title for the password reset form',
  })

  const enterNewPasswordText = intl.formatMessage({
    id: 'jNT4k88vUn',
    defaultMessage: 'Enter a new password',
    description: 'Instruction text on password reset form',
  })

  const newPasswordLabel = intl.formatMessage({
    id: 'g7bZZg/Wqs',
    defaultMessage: 'New password',
    description: 'Label for new password input on reset form',
  })

  const newPasswordPlaceholder = intl.formatMessage({
    id: 'gO3DEG2t6U',
    defaultMessage: 'New password',
    description: 'Placeholder for new password input on reset form',
  })

  const passwordRequiredError = intl.formatMessage({
    id: 'pIsIt2WkoU',
    defaultMessage: 'Password is required',
    description: 'Validation error when new password field is empty during reset',
  })

  const minCharsHelp = intl.formatMessage({
    id: 'cK/P/mdouZ',
    defaultMessage: 'Minimum 8 characters',
    description: 'Help text showing minimum password length requirement',
  })

  const confirmPasswordLabel = intl.formatMessage({
    id: 'ry4wzas14x',
    defaultMessage: 'Confirm new password',
    description: 'Label for confirm password input on reset form',
  })

  const confirmPasswordPlaceholder = intl.formatMessage({
    id: 'OxKJdOir0l',
    defaultMessage: 'Confirm new password',
    description: 'Placeholder for confirm password input on reset form',
  })

  const confirmPasswordRequired = intl.formatMessage({
    id: '3S1vJ6axJ1',
    defaultMessage: 'Password confirmation is required',
    description: 'Validation error when confirm password field is empty during reset',
  })

  const reenterHelp = intl.formatMessage({
    id: '1lLnCN2jJV',
    defaultMessage: 'Re-enter the new password',
    description: 'Help text for confirm password field during reset',
  })

  const changePasswordLabel = intl.formatMessage({
    id: 'w7XFTdd2eK',
    defaultMessage: 'Change password',
    description: 'Primary button text to submit password reset form',
  })

  return (<form onSubmit={onSubmit} name="forgotPasswordForm">
    <ModalHeader
      title={modalTitle}
      dismissible={dismissable}
    />
    <ModalBody>
      <ModalContent>
        <VerticalSpacer gap={24}>
          <Group direction="vertical" gap={20}>
            {error?.message && <MessageBanner kind="critical">{error.message}</MessageBanner>}
            {passwordError && <MessageBanner kind="critical">{passwordError}</MessageBanner>}
            <BodyText variant="large">
              {enterNewPasswordText}
            </BodyText>
          </Group>
          <TextInput
            id="password"
            type="password"
            name="password"
            label={newPasswordLabel}
            placeholder={newPasswordPlaceholder}
            requiredErrorMessage={passwordRequiredError}
            ref={firstInput}
            minLength={8}
            required
            helpText={minCharsHelp}
          />
          <TextInput
            id="confirmPassword"
            type="password"
            name="confirmPassword"
            label={confirmPasswordLabel}
            placeholder={confirmPasswordPlaceholder}
            requiredErrorMessage={confirmPasswordRequired}
            minLength={8}
            required
            helpText={reenterHelp}
          />
        </VerticalSpacer>
      </ModalContent>
    </ModalBody>
    <ModalFooter
      primaryActionProps={{
        children: changePasswordLabel,
        type: 'submit',
      }}
    />
  </form>)
}

function mapStateToProps(state: App.State) {
  return {
    error: state.auth.error,
  }
}

export default connect(mapStateToProps)(AccountAccessResetPassword)
